import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Typography from 'ui/src/components/Typography'
import Box from 'ui/src/components/Box'
import Tabs from 'ui/src/components/Tabs'
import Tab from 'ui/src/components/Tab'
import IconButton from 'ui/src/components/IconButton'
import Services from 'services'
import { useNavigate } from 'react-router-dom'
import { SettingsIcon, ChecklistRtlIcon, NotificationsOffIcon } from 'ui/src/assets/icons'
import { NotificationsMenuStyle } from './styles'
import globalStore from 'store'
import Card from '../Card'
import { SWR_RESPONSE_PLAN } from 'services/swr.keys'
import useSWR from 'swr'
import Skeleton from '@mui/material/Skeleton'

const reportResolutionArray = [
  'ACCEPTED',
  'WAITING FEEDBACK',
  'FAILED',
  'REMOVE',
  'REQUEST DENIED',
  'SOLVED',
  'PENDING',
]

const Menu = ({ isOpen, allNotifications, setRefetchData, setIsOpen }) => {
  const { t } = useTranslation()
  const user = globalStore(state => state.user)
  const isHighLevelUser = globalStore(state => state.isHighLevelUser)
  const register = globalStore(state => state.register)
  const navigate = useNavigate()

  const hasHighLevelPrivileges = isHighLevelUser()

  const [tabState, setTabState] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  const { data: haveResponsePlan } = useSWR(SWR_RESPONSE_PLAN + user?.email, () =>
    Services.ResponsePlan.getResponsePlan(),
  )

  useEffect(() => {
    if (!isOpen) {
      setTabState(0)
    }
  }, [isOpen])

  useEffect(() => {
    setIsLoading(true)
    if (
      allNotifications !== undefined &&
      register !== undefined &&
      haveResponsePlan !== undefined
    ) {
      setIsLoading(false)
    }
  }, [allNotifications, haveResponsePlan, register])

  const score = register?.lastScore

  const unreadNotifications = allNotifications?.filter(item => !item.isRead)

  const settingsNav = hasHighLevelPrivileges
    ? '/company/settings#notifications'
    : '/account-settings#notifications'

  const responsePlanRedirect = haveResponsePlan
    ? '/response-plan/dashboard'
    : '/response-plan/introduction'

  const updateNotification = (_id: string, isRead: boolean) => {
    const notification = { isRead }
    return Services.Notifications.patchNotificationById(_id, notification)
  }

  const handleClick = () => {
    navigate(settingsNav)
    setIsOpen(false)
  }

  const renderCard = () => {
    if (isLoading) {
      return (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              padding: 2,
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
              }}
            >
              <Skeleton variant="rectangular" height={24} width={24} />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '100%',
              }}
            >
              <Skeleton variant="rounded" height={15} width={100} />
              <Skeleton variant="rounded" height={10} width={'80%'} />
              <Skeleton variant="rounded" height={10} width={'80%'} />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              padding: 2,
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
              }}
            >
              <Skeleton variant="rectangular" height={24} width={24} />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '100%',
              }}
            >
              <Skeleton variant="rounded" height={15} width={100} />
              <Skeleton variant="rounded" height={10} width={'80%'} />
              <Skeleton variant="rounded" height={10} width={'80%'} />
            </Box>
          </Box>
        </>
      )
    } else {
      return (
        <>
          {tabState === 1 ? (
            <Box>
              {allNotifications?.length > 0 ? (
                allNotifications?.map((notification, i) => {
                  const isValidImg =
                    notification.data?.s3Url?.endsWith('.jpg') ||
                    notification.data?.s3Url?.endsWith('.jpeg') ||
                    notification.data?.s3Url?.endsWith('.png') ||
                    notification.data?.s3Url?.endsWith('.gif') ||
                    notification.data?.s3Url?.endsWith('.svg') ||
                    notification.data?.s3Url?.endsWith('.bmp') ||
                    notification.data?.s3Url?.endsWith('.tiff') ||
                    notification.data?.s3Url?.endsWith('.psd') ||
                    notification.data?.s3Url?.endsWith('.raw') ||
                    notification.data?.s3Url?.endsWith('.webp')

                  if (notification.notificationType === 'REPORT_STATUS') {
                    return (
                      notification.data.s3Url &&
                      notification.data.reportResolution &&
                      isValidImg &&
                      reportResolutionArray.includes(notification.data.reportResolution) && (
                        <Card
                          updateNotification={updateNotification}
                          setRefetchData={setRefetchData}
                          key={i}
                          notification={notification}
                          setIsNotificationsOpen={setIsOpen}
                          score={score}
                          responsePlanRedirect={responsePlanRedirect}
                        />
                      )
                    )
                  } else {
                    return (
                      <Card
                        updateNotification={updateNotification}
                        setRefetchData={setRefetchData}
                        key={i}
                        notification={notification}
                        setIsNotificationsOpen={setIsOpen}
                        score={score}
                        responsePlanRedirect={responsePlanRedirect}
                      />
                    )
                  }
                })
              ) : (
                <Box
                  sx={{
                    p: '1.5rem 1rem 3.5rem 1rem',
                    width: '100%',
                    height: '183px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.125,
                  }}
                >
                  <NotificationsOffIcon
                    sx={{ color: 'primary.100', width: '2.06rem', height: '2.15rem' }}
                  />
                  <Typography
                    sx={{
                      fontSize: 'h7Str.fontSize',
                      fontWeight: 'h7Str.fontWeight',
                      color: 'gray.700',
                      mt: 0.875,
                    }}
                  >
                    {t('notifications.emptyState.all.title')}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'gray.700',
                      fontSize: 14,
                    }}
                  >
                    {t('notifications.emptyState.all.description')}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box>
              {unreadNotifications.length > 0 ? (
                unreadNotifications.map((notification, i) => {
                  const isValidImg =
                    notification.data?.s3Url?.endsWith('.jpg') ||
                    notification.data?.s3Url?.endsWith('.jpeg') ||
                    notification.data?.s3Url?.endsWith('.png') ||
                    notification.data?.s3Url?.endsWith('.gif') ||
                    notification.data?.s3Url?.endsWith('.svg') ||
                    notification.data?.s3Url?.endsWith('.bmp') ||
                    notification.data?.s3Url?.endsWith('.tiff') ||
                    notification.data?.s3Url?.endsWith('.psd') ||
                    notification.data?.s3Url?.endsWith('.raw') ||
                    notification.data?.s3Url?.endsWith('.webp')

                  if (notification.notificationType === 'REPORT_STATUS') {
                    return (
                      notification.data.s3Url &&
                      notification.data.reportResolution &&
                      isValidImg &&
                      reportResolutionArray.includes(notification.data.reportResolution) && (
                        <Card
                          updateNotification={updateNotification}
                          setRefetchData={setRefetchData}
                          key={i}
                          notification={notification}
                          setIsNotificationsOpen={setIsOpen}
                          score={score}
                          responsePlanRedirect={responsePlanRedirect}
                        />
                      )
                    )
                  } else {
                    return (
                      <Card
                        updateNotification={updateNotification}
                        setRefetchData={setRefetchData}
                        key={i}
                        notification={notification}
                        setIsNotificationsOpen={setIsOpen}
                        score={score}
                        responsePlanRedirect={responsePlanRedirect}
                      />
                    )
                  }
                })
              ) : (
                <Box
                  sx={{
                    p: '1.5rem 1rem 3.5rem 1rem',
                    width: '100%',
                    height: '183px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.125,
                  }}
                >
                  <ChecklistRtlIcon
                    sx={{ color: 'primary.100', width: '2.5rem', height: '2.5rem' }}
                  />
                  <Typography
                    sx={{
                      fontSize: 'h7Str.fontSize',
                      fontWeight: 'h7Str.fontWeight',
                      mt: 0.875,
                    }}
                  >
                    {t('notifications.emptyState.unread.title')}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 'body3.fontSize',
                    }}
                  >
                    {t('notifications.emptyState.unread.description')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </>
      )
    }
  }

  return (
    <>
      {isOpen && (
        <NotificationsMenuStyle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              padding: '1rem 1rem 0.5rem 1rem',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: 'gray.900',
              }}
            >
              {t('notifications.notificationsModal.title')}
            </Typography>
            <IconButton sx={{ p: 0 }} onClick={handleClick}>
              <SettingsIcon sx={{ color: 'gray.400', size: 3 }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              gap: 2,
            }}
          >
            <Tabs
              value={tabState}
              onChange={(_, newValue) => {
                setTabState(newValue)
              }}
              TabIndicatorProps={{
                sx: {
                  bottom: 10,
                  display: 'flex',
                  justifyContent: 'center',
                  '& > span': {
                    maxWidth: '100%',
                    width: '100%',
                  },
                },
              }}
              sx={{
                '& .MuiTabs-flexContainer': {
                  flexWrap: 'wrap',
                },
              }}
            >
              <Tab
                value={0}
                label={t('notifications.notificationsModal.tabs.unread')}
                sx={{
                  fontSize: 'h7.fontSize',
                  textTransform: 'none',
                  fontWeight: 'h7.fontWeight',
                  color: 'gray.800',
                  p: 0,
                  minWidth: 0,
                  marginLeft: 2,
                }}
              />
              <Tab
                value={1}
                label={t('notifications.notificationsModal.tabs.all')}
                sx={{
                  fontSize: 'h7.fontSize',
                  textTransform: 'none',
                  fontWeight: 'h7.fontWeight',
                  color: 'gray.800',
                  p: 0,
                  minWidth: 0,
                  marginLeft: 4,
                }}
              />
            </Tabs>
          </Box>
          <Box
            sx={{
              width: '100%',
              minHeight: '183px',
            }}
          >
            {renderCard()}
          </Box>
        </NotificationsMenuStyle>
      )}
    </>
  )
}

export default Menu
